import { router, usePage } from "@inertiajs/react";
import { Badge, Button, Callout, Card, DropdownMenu, Flex, Heading, Table, Text } from "@radix-ui/themes";
import { Clock, Info, Lock } from "lucide-react";
import { badgeColors } from "@/shared/constants/constants";
import WithdrawFromEventButton from "@/shared/WithdrawFromEventButton";
import SignInLinkForm from "@/shared/users/SignInLinkForm";
import type {
  EventsShowApplication,
  EventsShowEvent,
  EventsShowParticipant,
  EventsShowParticipantType,
} from "@/types/events_show";

// One card per accepted type. `setupHref` carries the event they're standing
// on through the setup form, which hands them back to this event's
// application form when they're done (ReturnsToEvent).
const ROLES: Record<
  EventsShowParticipantType,
  {
    label: string;
    applyLabel: string;
    /** Shown when they have nothing to apply with yet. */
    missing: string;
    setupLabel: string;
    setupHref: (eventId: number) => string;
  }
> = {
  Driver: {
    label: "Driver",
    applyLabel: "Apply as driver",
    missing: "No car in your garage yet.",
    setupLabel: "Add a car",
    setupHref: (eventId) => `/driver_cars/new?event_id=${eventId}`,
  },
  "Media Group": {
    label: "Media",
    applyLabel: "Apply as media",
    missing: "No media profile yet.",
    setupLabel: "Set up your media profile",
    setupHref: (eventId) => `/media_group_participant/new?event_id=${eventId}`,
  },
  Staff: {
    label: "Staff",
    applyLabel: "Apply as staff",
    missing: "No staff profile yet.",
    setupLabel: "Set up your staff profile",
    setupHref: (eventId) => `/staff_participant/new?event_id=${eventId}`,
  },
  Team: {
    label: "Team",
    applyLabel: "Apply as team",
    missing: "You don't have a team yet.",
    setupLabel: "Create a team",
    setupHref: (eventId) => `/team_participant/new?event_id=${eventId}`,
  },
};

// The order the cards appear in, whatever order the event lists them.
const ROLE_ORDER: EventsShowParticipantType[] = ["Driver", "Media Group", "Staff", "Team"];

interface Props {
  event: EventsShowEvent;
  participants: EventsShowParticipant[];
  applications: EventsShowApplication[];
}

/**
 * Applying to an event: where applications stand, and what the viewer can do
 * about it. Signed out, the way in is an emailed sign-in link (guests can
 * still apply as media or staff without one). Signed in, one card per role the event takes, each saying
 * where they stand: apply, already applied, or what they need first.
 *
 * Renders nothing for an event that takes no applications.
 */
export default function ApplyCard({ event, participants, applications }: Props) {
  const { props } = usePage();
  const signedIn = !!props.currentUser;
  const status = event.applications_status;

  if (status === "not_configured") return null;

  const returnTo = `return_to=/events/${event.id}`;
  const takesGuests = event.accepted_types["Media Group"] || event.accepted_types.Staff;

  return (
    <Card size="3" id="apply" style={{ boxShadow: "inset 0 0 0 2px var(--accent-9)" }}>
      <Flex direction="column" gap="4">
        <Flex justify="between" align="baseline" gap="2" wrap="wrap">
          <Heading as="h2" size="5">Apply to this event</Heading>
          {status === "open" && <Badge color="green" variant="soft">Open</Badge>}
        </Flex>

        {status === "not_open_yet" && (
          <Callout.Root color="gray" variant="soft">
            <Callout.Icon>
              <Clock size={16} />
            </Callout.Icon>
            <Callout.Text>
              Applications open on <Text weight="bold">{event.applications_open_at_display}</Text>.
              {!signedIn && (
                <>
                  {" "}
                  <a href={`/users/sign_in?${returnTo}`} className="text-accent11">Sign in</a> now so you're
                  ready — and to add this event to your watchlist.
                </>
              )}
            </Callout.Text>
          </Callout.Root>
        )}

        {status === "closed" && (
          <Callout.Root color="gray" variant="soft">
            <Callout.Icon>
              <Lock size={16} />
            </Callout.Icon>
            <Callout.Text>Applications for this event are now closed.</Callout.Text>
          </Callout.Root>
        )}

        {/* Media at this track carry an annual credential — the apply flow collects it */}
        {status === "open" && event.accepted_types["Media Group"] && event.track.credential_categories.length > 0 && (
          <Callout.Root color="gray" variant="soft">
            <Callout.Icon>
              <Info size={16} />
            </Callout.Icon>
            <Callout.Text>
              {event.track.name} asks media to complete an annual form (
              {event.track.credential_categories.join(", ")}) before shooting. If you haven't this year, you'll
              fill it out as part of applying — it expires December 31.
            </Callout.Text>
          </Callout.Root>
        )}

        {status === "open" && !signedIn && (
          <Flex direction="column" gap="3">
            <Text as="p" size="3">
              Enter your email and we'll send you a link to apply. You can follow and withdraw your application any
              time, and apply to the next event in a click.
            </Text>
            <SignInLinkForm returnTo={`/events/${event.id}`} />
            <Text size="2" color="gray">
              Use a password?{" "}
              <a href={`/users/sign_in?${returnTo}`} className="text-accent11">
                Sign in
              </a>
            </Text>
            {takesGuests && (
              <Text size="2" color="gray">
                Media or staff?{" "}
                <a href={`/events/${event.id}/apply`} className="text-accent11">
                  Apply without an account
                </a>
              </Text>
            )}
          </Flex>
        )}

        {status === "open" && signedIn && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {ROLE_ORDER.filter((type) => event.accepted_types[type]).map((type) => (
              <RoleCard
                key={type}
                eventId={event.id}
                type={type}
                options={participants.filter((p) => p.type === type)}
                applications={applications}
              />
            ))}
          </div>
        )}

        {applications.length > 0 && (
          <YourApplications applications={applications} canWithdraw={status === "open"} />
        )}
      </Flex>
    </Card>
  );
}

function RoleCard({
  eventId,
  type,
  options,
  applications,
}: {
  eventId: number;
  type: EventsShowParticipantType;
  options: EventsShowParticipant[];
  applications: EventsShowApplication[];
}) {
  const role = ROLES[type];
  const applicationFor = (participant: EventsShowParticipant) =>
    applications.find((a) => a.participant.id === participant.id);
  const applyHref = (participantId: number) =>
    `/events/${eventId}/applications/new?participant_id=${participantId}`;

  let detail: string;
  let action: React.ReactNode;

  if (options.length === 0) {
    detail = role.missing;
    action = (
      <Button asChild variant="soft">
        <a href={role.setupHref(eventId)}>{role.setupLabel}</a>
      </Button>
    );
  } else if (options.length === 1) {
    const [only] = options;
    const applied = applicationFor(only);
    detail = `As ${only.name}`;
    action = applied ? (
      <Badge size="2" variant="soft" color={badgeColors[applied.status]} style={{ textTransform: "capitalize" }}>
        Applied · {applied.status}
      </Badge>
    ) : (
      <Button onClick={() => router.visit(applyHref(only.id))}>{role.applyLabel}</Button>
    );
  } else {
    // A menu only when there's a real choice: several cars, or more than one team.
    const applied = options.filter((p) => applicationFor(p));
    detail = applied.length > 0
      ? `Applied with ${applied.length} of your ${options.length}`
      : `Pick one of your ${options.length}`;
    action = (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <Button>
            {role.applyLabel}
            <DropdownMenu.TriggerIcon />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          {options.map((participant) => (
            <DropdownMenu.Item
              key={participant.id}
              disabled={!!applicationFor(participant)}
              onSelect={() => router.visit(applyHref(participant.id))}
            >
              {participant.name}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-gray5 p-4">
      <Text weight="bold" size="3">{role.label}</Text>
      <Text size="2" color="gray" className="flex-1">{detail}</Text>
      <div>{action}</div>
    </div>
  );
}

function YourApplications({
  applications,
  canWithdraw,
}: {
  applications: EventsShowApplication[];
  canWithdraw: boolean;
}) {
  return (
    <Flex direction="column" gap="2">
      <Heading as="h3" size="3">Your applications for this event</Heading>
      <Table.Root size="1">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Applied as</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
            {canWithdraw && (
              <Table.ColumnHeaderCell>
                <span className="sr-only">Actions</span>
              </Table.ColumnHeaderCell>
            )}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {applications.map((application) => (
            <Table.Row key={application.id} align="center">
              <Table.RowHeaderCell>{application.participant.name}</Table.RowHeaderCell>
              <Table.Cell>
                <Badge variant="soft" color={badgeColors[application.status]} style={{ textTransform: "capitalize" }}>
                  {application.status}
                </Badge>
              </Table.Cell>
              {canWithdraw && (
                <Table.Cell justify="end">
                  <WithdrawFromEventButton application={application} />
                </Table.Cell>
              )}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Flex>
  );
}
